import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "./../../assets/cambridge-carpenter.png";
import { services } from "../../data/services";
import { SITE } from "../../data/site";
import Icon from "../UI/Icon";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/carpentry-services", label: "Services", hasMenu: true },
  { to: "/site-carpentry", label: "Site Carpentry" },
  { to: "/areas", label: "Areas" },
  { to: "/our-work", label: "Our Work" },
  { to: "/about-us", label: "About" },
  { to: "/contact", label: "Contact" },
];

const Navbar = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();

  // Close menus on navigation
  useEffect(() => {
    setDrawerOpen(false);
    setServicesOpen(false);
    setMobileServicesOpen(false);
  }, [location]);

  // Lock body scroll when the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const openServices = () => {
    clearTimeout(closeTimer.current);
    setServicesOpen(true);
  };
  const closeServices = () => {
    closeTimer.current = setTimeout(() => setServicesOpen(false), 150);
  };

  const linkClass = ({ isActive }) =>
    `relative py-1 text-sm font-medium tracking-wide transition-colors hover:text-brass ${
      isActive ? "text-brass" : "text-paper"
    }`;

  return (
    <div className="header absolute top-0 left-0 right-0 z-40">
      {/* Top contact bar */}
      <div className="bg-ink flex items-center justify-center w-full h-[30px] lg:h-[40px] px-4">
        <span className="text-xs lg:text-sm tracking-wide uppercase font-bold text-paper">
          Call us now{" "}
          <a href={SITE.phoneHref} className="text-brass underline underline-offset-2">
            {SITE.phone}
          </a>
        </span>
      </div>

      {/* Main bar */}
      <div className="bg-bark/95 backdrop-blur supports-[backdrop-filter]:bg-bark/80 border-b border-white/10">
        <div className="max-w-site mx-auto flex items-center justify-between gap-4 py-3 px-5 sm:px-10">
          {/* Mobile burger */}
          <button
            onClick={() => setDrawerOpen(true)}
            className="lg:hidden text-paper p-1"
            aria-label="Open navigation menu"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 lg:mr-auto">
            <img src={logo} alt="Cambridge Carpenter" className="w-[180px] lg:w-[210px]" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map((item) =>
              item.hasMenu ? (
                <div
                  key={item.to}
                  className="relative"
                  onMouseEnter={openServices}
                  onMouseLeave={closeServices}
                >
                  <NavLink to={item.to} className={linkClass} end={false}>
                    <span className="flex items-center gap-1">
                      {item.label}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`transition-transform ${servicesOpen ? "rotate-180" : ""}`}>
                        <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </NavLink>
                  {servicesOpen && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[520px]">
                      <div className="bg-paper border border-hairline shadow-xl p-3 grid grid-cols-2 gap-1">
                        {services.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/carpentry-services/${s.slug}`}
                            className="group flex items-start gap-3 p-3 hover:bg-linen transition-colors"
                          >
                            <span className="mt-0.5 w-9 h-9 shrink-0 flex items-center justify-center bg-oak/10 text-oak">
                              <Icon name={s.icon} className="w-5 h-5" />
                            </span>
                            <span>
                              <span className="block font-display font-semibold text-ink text-[15px] leading-tight group-hover:text-oak transition-colors">
                                {s.navLabel}
                              </span>
                            </span>
                          </Link>
                        ))}
                        <Link
                          to="/carpentry-services"
                          className="col-span-2 mt-1 text-center bg-ink text-paper py-3 text-sm font-bold uppercase tracking-wide hover:bg-oak transition-colors"
                        >
                          View all services
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
                  {item.label}
                </NavLink>
              )
            )}
            <Link to="/contact" className="btn-primary !py-2.5 !px-5 text-xs">
              Free Quote
            </Link>
          </nav>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${
          drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-ink/60" onClick={() => setDrawerOpen(false)} />
        <div
          className={`absolute left-0 top-0 bottom-0 w-[84vw] max-w-sm bg-paper shadow-2xl transition-transform duration-300 overflow-y-auto ${
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between p-5 border-b border-hairline bg-bark">
            <img src={logo} alt="Cambridge Carpenter" className="w-[170px]" />
            <button onClick={() => setDrawerOpen(false)} className="text-paper p-1" aria-label="Close menu">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          </div>
          <nav className="py-2">
            {navItems.map((item) =>
              item.hasMenu ? (
                <div key={item.to} className="border-b border-hairline">
                  <div className="flex items-center justify-between">
                    <Link to={item.to} className="flex-1 py-4 px-6 text-ink font-medium">
                      {item.label}
                    </Link>
                    <button
                      onClick={() => setMobileServicesOpen((v) => !v)}
                      className="py-4 px-6 text-oak"
                      aria-label="Toggle services list"
                      aria-expanded={mobileServicesOpen}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className={`transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`}>
                        <path d="m6 9 6 6 6-6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                  {mobileServicesOpen && (
                    <div className="bg-linen">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/carpentry-services/${s.slug}`}
                          className="block py-3 px-10 text-bark text-[15px] border-t border-hairline"
                        >
                          {s.navLabel}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  className="block py-4 px-6 text-ink font-medium border-b border-hairline"
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>
          <div className="p-6">
            <Link to="/contact" className="btn-primary w-full">
              Get a free quote
            </Link>
            <a href={SITE.phoneHref} className="flex items-center justify-center gap-2 mt-4 text-ink font-bold">
              <Icon name="phone" className="w-4 h-4 text-oak" /> {SITE.phone}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
